import { db, storage } from './config';
import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import ImageCompressionUtils from './ImageCompressionUtils';

/**
 * Servicio para gestión de carta y productos usando Firebase
 * VERSIÓN CON PRECIOS MÚLTIPLES Y COMPRESIÓN AUTOMÁTICA DE IMÁGENES
 */
const MenuService = {
  /**
   * 🆕 FUNCIÓN AUXILIAR: Procesar y subir imagen optimizada
   */
  _processAndUploadImage: async (imageFile, folder, id) => {
    try {
      console.log(`📸 Procesando imagen para ${folder}/${id}...`);
      
      // Validar archivo
      const validation = ImageCompressionUtils.isValidImageFile(imageFile);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Procesar imagen (compresión + thumbnail)
      const processed = await ImageCompressionUtils.processImage(imageFile);
      
      console.log(`✅ Imagen procesada - Reducción: ${processed.compressed.reduction}% (${Math.round(processed.original.info.size/1024)}KB → ${Math.round(processed.compressed.size/1024)}KB)`);

      // Generar nombres de archivo optimizados
      const mainFileName = ImageCompressionUtils.generateOptimizedFileName(
        `${folder}_${id}`, true, false
      );
      const thumbnailFileName = ImageCompressionUtils.generateOptimizedFileName(
        `${folder}_${id}`, true, true
      );

      // Subir imagen principal comprimida
      const mainRef = ref(storage, `${folder}/${mainFileName}`);
      await uploadBytes(mainRef, processed.compressed.blob);
      const mainUrl = await getDownloadURL(mainRef);

      // Subir thumbnail
      const thumbnailRef = ref(storage, `${folder}/thumbnails/${thumbnailFileName}`);
      await uploadBytes(thumbnailRef, processed.thumbnail.blob);
      const thumbnailUrl = await getDownloadURL(thumbnailRef);

      console.log(`🚀 Imagen subida exitosamente: ${mainUrl}`);

      return {
        mainUrl,
        thumbnailUrl,
        optimization: {
          originalSize: processed.original.info.size,
          compressedSize: processed.compressed.size,
          reduction: processed.compressed.reduction,
          optimizedAt: new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('❌ Error procesando imagen:', error);
      throw new Error(`Error al procesar imagen: ${error.message}`);
    }
  },

  /**
   * 🆕 FUNCIÓN AUXILIAR: Eliminar imágenes existentes
   */
  _deleteExistingImages: async (mainImageUrl, thumbnailUrl = null) => {
    const deletePromises = [];

    if (mainImageUrl && mainImageUrl.includes('firebasestorage')) {
      try {
        const mainRef = ref(storage, mainImageUrl);
        deletePromises.push(deleteObject(mainRef));
      } catch (error) {
        console.warn('⚠️ No se pudo preparar eliminación de imagen principal:', error);
      }
    }

    if (thumbnailUrl && thumbnailUrl.includes('firebasestorage')) {
      try {
        const thumbRef = ref(storage, thumbnailUrl);
        deletePromises.push(deleteObject(thumbRef));
      } catch (error) {
        console.warn('⚠️ No se pudo preparar eliminación de thumbnail:', error);
      }
    }

    if (deletePromises.length > 0) {
      try {
        await Promise.all(deletePromises);
        console.log('🗑️ Imágenes anteriores eliminadas correctamente');
      } catch (error) {
        console.warn('⚠️ Error eliminando algunas imágenes anteriores:', error);
      }
    }
  },

  /**
   * Obtener todas las categorías ordenadas (COMPATIBLE CON DATOS ANTIGUOS)
   */
  getCategories: async () => {
    try {
      const categoriesRef = collection(db, 'categories');
      const querySnapshot = await getDocs(categoriesRef);
      
      const categories = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          order: data.order || 999,
          icon: data.icon || '📋',
          active: data.active !== undefined ? data.active : true
        };
      });
      
      return categories.sort((a, b) => {
        if (a.order !== b.order) {
          return a.order - b.order;
        }
        return a.name.localeCompare(b.name);
      });
    } catch (error) {
      console.error('Error al obtener categorías:', error);
      throw error;
    }
  },

  /**
   * Crear una nueva categoría con compresión automática de imagen
   */
  createCategory: async (categoryData) => {
    try {
      // 🆕 Procesar imagen si existe
      if (categoryData.imageFile) {
        const tempId = `temp_${Date.now()}`;
        const imageResult = await MenuService._processAndUploadImage(
          categoryData.imageFile, 
          'categories', 
          tempId
        );
        
        categoryData.image = imageResult.mainUrl;
        categoryData.thumbnailImage = imageResult.thumbnailUrl;
        categoryData.imageOptimization = imageResult.optimization;
        categoryData.imageOptimized = true;
        
        delete categoryData.imageFile;
      }
      
      if (!categoryData.order) {
        const categories = await MenuService.getCategories();
        categoryData.order = categories.length > 0 ? Math.max(...categories.map(c => c.order || 0)) + 1 : 1;
      }
      
      if (!categoryData.icon) {
        categoryData.icon = '📋';
      }

      if (categoryData.active === undefined) {
        categoryData.active = true;
      }
      
      const docRef = await addDoc(collection(db, 'categories'), categoryData);
      
      // 🆕 Si teníamos un ID temporal, actualizar las imágenes con el ID real
      if (categoryData.imageFile && categoryData.image) {
        try {
          const realImageResult = await MenuService._processAndUploadImage(
            categoryData.imageFile, 
            'categories', 
            docRef.id
          );
          
          await updateDoc(docRef, {
            image: realImageResult.mainUrl,
            thumbnailImage: realImageResult.thumbnailUrl,
            imageOptimization: realImageResult.optimization
          });
          
          categoryData.image = realImageResult.mainUrl;
          categoryData.thumbnailImage = realImageResult.thumbnailUrl;
        } catch (error) {
          console.warn('⚠️ Error actualizando imágenes con ID real:', error);
        }
      }
      
      return {
        id: docRef.id,
        ...categoryData
      };
    } catch (error) {
      console.error('Error al crear categoría:', error);
      throw error;
    }
  },

  /**
   * Actualizar una categoría con compresión automática de imagen
   */
  updateCategory: async (categoryId, categoryData) => {
    try {
      // 🆕 Procesar nueva imagen si existe
      if (categoryData.imageFile) {
        // Obtener datos actuales para eliminar imágenes anteriores
        const currentCategory = await getDoc(doc(db, 'categories', categoryId));
        const currentData = currentCategory.exists() ? currentCategory.data() : {};
        
        // Eliminar imágenes anteriores
        await MenuService._deleteExistingImages(
          currentData.image, 
          currentData.thumbnailImage
        );
        
        // Procesar y subir nueva imagen
        const imageResult = await MenuService._processAndUploadImage(
          categoryData.imageFile, 
          'categories', 
          categoryId
        );
        
        categoryData.image = imageResult.mainUrl;
        categoryData.thumbnailImage = imageResult.thumbnailUrl;
        categoryData.imageOptimization = imageResult.optimization;
        categoryData.imageOptimized = true;
        
        delete categoryData.imageFile;
      }
      
      const categoryRef = doc(db, 'categories', categoryId);
      await updateDoc(categoryRef, categoryData);
      
      return {
        id: categoryId,
        ...categoryData
      };
    } catch (error) {
      console.error(`Error al actualizar categoría ${categoryId}:`, error);
      throw error;
    }
  },

  /**
   * Eliminar una categoría (actualizada)
   */
  deleteCategory: async (categoryId) => {
    try {
      const productsRef = collection(db, 'products');
      const q = query(productsRef, where('categoryId', '==', categoryId));
      const productsSnapshot = await getDocs(q);
      
      if (!productsSnapshot.empty) {
        throw new Error('No se puede eliminar la categoría porque tiene productos asociados. Elimina o reasigna los productos primero.');
      }
      
      const categoryRef = doc(db, 'categories', categoryId);
      const categorySnap = await getDoc(categoryRef);
      
      if (categorySnap.exists()) {
        const categoryData = categorySnap.data();
        
        // 🆕 Eliminar tanto imagen principal como thumbnail
        await MenuService._deleteExistingImages(
          categoryData.image, 
          categoryData.thumbnailImage
        );
      }
      
      await deleteDoc(categoryRef);
      return { id: categoryId };
    } catch (error) {
      console.error(`Error al eliminar categoría ${categoryId}:`, error);
      throw error;
    }
  },

  /**
   * Reordenar categorías
   */
  reorderCategories: async (categoryIds) => {
    try {
      const batch = writeBatch(db);
      
      categoryIds.forEach((categoryId, index) => {
        const categoryRef = doc(db, 'categories', categoryId);
        batch.update(categoryRef, { order: index + 1 });
      });
      
      await batch.commit();
      return { success: true };
    } catch (error) {
      console.error('Error al reordenar categorías:', error);
      throw error;
    }
  },

  /**
   * MIGRACIÓN: Actualizar categorías existentes con campos faltantes
   */
  migrateCategoriesData: async (options = {}) => {
    try {
      console.log('🔄 Iniciando migración de categorías...');
      
      const categoriesRef = collection(db, 'categories');
      const querySnapshot = await getDocs(categoriesRef);
      
      if (querySnapshot.empty) {
        return { success: true, message: 'No hay categorías para migrar', migratedCount: 0 };
      }

      const batch = writeBatch(db);
      let migratedCount = 0;
      
      const suggestedIcons = {
        'bebidas': '🥤', 'drinks': '🥤', 'drink': '🥤',
        'entrantes': '🥗', 'starters': '🥗', 'starter': '🥗', 'appetizers': '🥗',
        'principales': '🍽️', 'main': '🍽️', 'mains': '🍽️', 'platos': '🍽️',
        'hamburguesas': '🍔', 'burgers': '🍔', 'burger': '🍔',
        'pizzas': '🍕', 'pizza': '🍕',
        'postres': '🍰', 'desserts': '🍰', 'dessert': '🍰', 'dulces': '🍰',
        'ensaladas': '🥗', 'salads': '🥗', 'salad': '🥗',
        'pasta': '🍝', 'pastas': '🍝', 'spaghetti': '🍝',
        'carnes': '🥩', 'meat': '🥩', 'carne': '🥩',
        'pescados': '🐟', 'fish': '🐟', 'pescado': '🐟', 'seafood': '🍤',
        'mariscos': '🍤', 'shrimp': '🍤', 'prawns': '🍤',
        'sopas': '🍜', 'soups': '🍜', 'soup': '🍜', 'sopa': '🍜',
        'tacos': '🌮', 'mexican': '🌮', 'mexicana': '🌮',
        'café': '☕', 'coffee': '☕', 'cafés': '☕',
        'vinos': '🍷', 'wine': '🍷', 'wines': '🍷', 'vino': '🍷',
        'cervezas': '🍺', 'beer': '🍺', 'beers': '🍺', 'cerveza': '🍺',
        'cocktails': '🍸', 'cócteles': '🍸', 'cocktail': '🍸',
        'jugos': '🧃', 'juices': '🧃', 'zumos': '🧃',
        'helados': '🍨', 'ice cream': '🍨', 'gelato': '🍨',
        'pan': '🍞', 'bread': '🍞', 'panadería': '🥖'
      };

      querySnapshot.docs.forEach((docSnapshot, index) => {
        const categoryData = docSnapshot.data();
        const categoryId = docSnapshot.id;
        
        const needsUpdate = !categoryData.order || !categoryData.icon || categoryData.active === undefined;
        
        if (needsUpdate) {
          const updates = {};
          
          if (!categoryData.order) {
            updates.order = index + 1;
          }
          
          if (!categoryData.icon) {
            const categoryName = categoryData.name?.toLowerCase() || '';
            let suggestedIcon = '📋';
            
            for (const [keyword, icon] of Object.entries(suggestedIcons)) {
              if (categoryName.includes(keyword)) {
                suggestedIcon = icon;
                break;
              }
            }
            
            updates.icon = suggestedIcon;
          }
          
          if (categoryData.active === undefined) {
            updates.active = true;
          }
          
          const categoryRef = doc(db, 'categories', categoryId);
          batch.update(categoryRef, updates);
          migratedCount++;
          
          console.log(`✅ Preparando migración para: ${categoryData.name} -> ${updates.icon || 'sin cambio'}`);
        }
      });

      if (migratedCount > 0) {
        await batch.commit();
        console.log(`🎉 Migración completada! ${migratedCount} categorías actualizadas.`);
        return { 
          success: true, 
          message: `Migración completada exitosamente. ${migratedCount} categorías actualizadas.`, 
          migratedCount 
        };
      } else {
        return { 
          success: true, 
          message: 'Todas las categorías ya tenían los campos requeridos.', 
          migratedCount: 0 
        };
      }
      
    } catch (error) {
      console.error('❌ Error durante la migración:', error);
      throw new Error(`Error en migración: ${error.message}`);
    }
  },

  /**
   * 🆕 MIGRACIÓN DE PRODUCTOS: Convertir precios simples a precios múltiples
   */
  migrateProductsPricing: async () => {
    try {
      console.log('🔄 Iniciando migración de precios de productos...');
      
      const productsRef = collection(db, 'products');
      const querySnapshot = await getDocs(productsRef);
      
      if (querySnapshot.empty) {
        return { success: true, message: 'No hay productos para migrar', migratedCount: 0 };
      }

      const batch = writeBatch(db);
      let migratedCount = 0;
      
      querySnapshot.docs.forEach((docSnapshot) => {
        const productData = docSnapshot.data();
        const productId = docSnapshot.id;
        
        // Verificar si ya tiene estructura de pricing múltiple
        const needsPricingMigration = !productData.pricing && productData.price !== undefined;
        
        if (needsPricingMigration) {
          const currentPrice = parseFloat(productData.price) || 0;
          
          const updates = {
            pricing: {
              local: currentPrice,
              takeaway: currentPrice, // Mismo precio por defecto
              delivery: currentPrice * 1.1 // 10% más caro para delivery por defecto
            }
            // Mantener el campo price original para compatibilidad
          };
          
          const productRef = doc(db, 'products', productId);
          batch.update(productRef, updates);
          migratedCount++;
          
          console.log(`✅ Migrando precios para: ${productData.name} (${currentPrice}€ -> local:${currentPrice}€, takeaway:${currentPrice}€, delivery:${(currentPrice * 1.1).toFixed(2)}€)`);
        }
      });

      if (migratedCount > 0) {
        await batch.commit();
        console.log(`🎉 Migración de precios completada! ${migratedCount} productos actualizados.`);
        return { 
          success: true, 
          message: `Migración de precios completada. ${migratedCount} productos actualizados.`, 
          migratedCount 
        };
      } else {
        return { 
          success: true, 
          message: 'Todos los productos ya tenían precios múltiples configurados.', 
          migratedCount: 0 
        };
      }
      
    } catch (error) {
      console.error('❌ Error durante la migración de precios:', error);
      throw new Error(`Error en migración de precios: ${error.message}`);
    }
  },

  /**
   * Obtener todos los productos con filtros opcionales y precios normalizados
   */
  getProducts: async (filters = {}) => {
    try {
      const productsRef = collection(db, 'products');
      
      let q = productsRef;
      const constraints = [];
      
      if (filters.categoryId) {
        constraints.push(where('categoryId', '==', filters.categoryId));
      }
      
      if (filters.active !== undefined) {
        constraints.push(where('active', '==', filters.active));
      }
      
      if (filters.availability) {
        constraints.push(where(`availability.${filters.availability}`, '==', true));
      }
      
      constraints.push(orderBy('name'));
      
      if (constraints.length > 0) {
        q = query(productsRef, ...constraints);
      } else {
        q = query(productsRef);
      }
      
      const querySnapshot = await getDocs(q);
      
      // Normalizar productos con estructura de precios
      const products = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Asegurar que tenga estructura de pricing
          pricing: data.pricing || {
            local: parseFloat(data.price) || 0,
            takeaway: parseFloat(data.price) || 0,
            delivery: parseFloat(data.price) * 1.1 || 0
          },
          // Mantener price para compatibilidad (usando local como referencia)
          price: data.pricing ? data.pricing.local : (parseFloat(data.price) || 0)
        };
      });
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        return products.filter(product => 
          product.name.toLowerCase().includes(searchLower) ||
          (product.description && product.description.toLowerCase().includes(searchLower))
        );
      }
      
      return products;
    } catch (error) {
      console.error('Error al obtener productos:', error);
      throw error;
    }
  },

  /**
   * Obtener un producto específico por ID con precios normalizados
   */
  getProductById: async (productId) => {
    try {
      const productRef = doc(db, 'products', productId);
      const productSnap = await getDoc(productRef);
      
      if (productSnap.exists()) {
        const data = productSnap.data();
        return {
          id: productId,
          ...data,
          // Normalizar precios
          pricing: data.pricing || {
            local: parseFloat(data.price) || 0,
            takeaway: parseFloat(data.price) || 0,
            delivery: parseFloat(data.price) * 1.1 || 0
          },
          price: data.pricing ? data.pricing.local : (parseFloat(data.price) || 0)
        };
      } else {
        throw new Error('Producto no encontrado');
      }
    } catch (error) {
      console.error(`Error al obtener producto ${productId}:`, error);
      throw error;
    }
  },

  /**
   * Crear un nuevo producto con precios múltiples y compresión automática
   */
  createProduct: async (productData) => {
    try {
      // 🆕 Procesar imagen si existe
      if (productData.imageFile) {
        const tempId = `temp_${Date.now()}`;
        const imageResult = await MenuService._processAndUploadImage(
          productData.imageFile, 
          'products', 
          tempId
        );
        
        productData.image = imageResult.mainUrl;
        productData.thumbnailImage = imageResult.thumbnailUrl;
        productData.imageOptimization = imageResult.optimization;
        productData.imageOptimized = true;
        
        delete productData.imageFile;
      }
      
      // Asegurar estructura de precios
      if (!productData.pricing && productData.price) {
        const basePrice = parseFloat(productData.price) || 0;
        productData.pricing = {
          local: basePrice,
          takeaway: basePrice,
          delivery: basePrice
        };
      }
      
      // Asegurar disponibilidad por defecto
      if (!productData.availability) {
        productData.availability = {
          local: true,
          takeaway: true,
          delivery: true
        };
      }

      if (productData.active === undefined) {
        productData.active = true;
      }
      
      const docRef = await addDoc(collection(db, 'products'), productData);
      
      // 🆕 Si teníamos imagen, actualizar con ID real
      if (productData.imageFile) {
        try {
          const realImageResult = await MenuService._processAndUploadImage(
            productData.imageFile, 
            'products', 
            docRef.id
          );
          
          await updateDoc(docRef, {
            image: realImageResult.mainUrl,
            thumbnailImage: realImageResult.thumbnailUrl,
            imageOptimization: realImageResult.optimization
          });
          
          productData.image = realImageResult.mainUrl;
          productData.thumbnailImage = realImageResult.thumbnailUrl;
        } catch (error) {
          console.warn('⚠️ Error actualizando imágenes con ID real:', error);
        }
      }
      
      return {
        id: docRef.id,
        ...productData
      };
    } catch (error) {
      console.error('Error al crear producto:', error);
      throw error;
    }
  },

  /**
   * Actualizar un producto con precios múltiples y compresión automática
   */
  updateProduct: async (productId, productData) => {
    try {
      // 🆕 Procesar nueva imagen si existe
      if (productData.imageFile) {
        // Obtener datos actuales para eliminar imágenes anteriores
        const currentProduct = await getDoc(doc(db, 'products', productId));
        const currentData = currentProduct.exists() ? currentProduct.data() : {};
        
        // Eliminar imágenes anteriores
        await MenuService._deleteExistingImages(
          currentData.image, 
          currentData.thumbnailImage
        );
        
        // Procesar y subir nueva imagen
        const imageResult = await MenuService._processAndUploadImage(
          productData.imageFile, 
          'products', 
          productId
        );
        
        productData.image = imageResult.mainUrl;
        productData.thumbnailImage = imageResult.thumbnailUrl;
        productData.imageOptimization = imageResult.optimization;
        productData.imageOptimized = true;
        
        delete productData.imageFile;
      }
      
      // Si se está actualizando desde el admin con estructura de precios múltiples
      if (productData.pricing) {
        // Actualizar también el campo price para compatibilidad (usar precio local)
        productData.price = productData.pricing.local;
      }
      
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, productData);
      
      return {
        id: productId,
        ...productData
      };
    } catch (error) {
      console.error(`Error al actualizar producto ${productId}:`, error);
      throw error;
    }
  },

  /**
   * 🆕 Actualizar precios de un producto
   */
  updateProductPricing: async (productId, pricing) => {
    try {
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, { 
        pricing,
        price: pricing.local // Mantener compatibilidad
      });
      
      return {
        id: productId,
        pricing
      };
    } catch (error) {
      console.error(`Error al actualizar precios del producto ${productId}:`, error);
      throw error;
    }
  },

  /**
   * Actualizar disponibilidad de un producto
   */
  updateProductAvailability: async (productId, availability) => {
    try {
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, { availability });
      
      return {
        id: productId,
        availability
      };
    } catch (error) {
      console.error(`Error al actualizar disponibilidad del producto ${productId}:`, error);
      throw error;
    }
  },

  /**
   * Cambiar estado activo/inactivo de un producto
   */
  toggleProductActive: async (productId, active) => {
    try {
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, { active });
      
      return {
        id: productId,
        active
      };
    } catch (error) {
      console.error(`Error al cambiar estado activo del producto ${productId}:`, error);
      throw error;
    }
  },

  /**
   * Eliminar un producto (actualizado)
   */
  deleteProduct: async (productId) => {
    try {
      const productRef = doc(db, 'products', productId);
      const productSnap = await getDoc(productRef);
      
      if (productSnap.exists()) {
        const productData = productSnap.data();
        
        // 🆕 Eliminar tanto imagen principal como thumbnail
        await MenuService._deleteExistingImages(
          productData.image, 
          productData.thumbnailImage
        );
      }
      
      await deleteDoc(productRef);
      return { id: productId };
    } catch (error) {
      console.error(`Error al eliminar producto ${productId}:`, error);
      throw error;
    }
  },
  
  /**
   * Obtener menú público con precios según tipo de pedido
   */
  getPublicMenu: async (type = 'local') => {
    try {
      const categories = await MenuService.getCategories();
      
      const filters = { active: true };
      if (type && type !== 'local') {
        filters.availability = type;
      }
      
      const products = await MenuService.getProducts(filters);
      
      // Adaptar precios según el tipo de pedido
      const productsWithCorrectPricing = products.map(product => ({
        ...product,
        // Usar el precio correcto según el tipo de pedido
        price: product.pricing ? (product.pricing[type] || product.pricing.local || product.price) : product.price,
        // Mantener toda la estructura de pricing para referencia
        originalPricing: product.pricing
      }));
      
      return {
        categories,
        products: productsWithCorrectPricing
      };
    } catch (error) {
      console.error('Error al obtener menú público:', error);
      throw error;
    }
  },

  /**
   * 🆕 Obtener producto con precio específico para tipo de pedido
   */
  getProductWithPricing: async (productId, orderType = 'local') => {
    try {
      const product = await MenuService.getProductById(productId);
      
      return {
        ...product,
        currentPrice: product.pricing ? (product.pricing[orderType] || product.pricing.local) : product.price,
        orderType
      };
    } catch (error) {
      console.error(`Error al obtener producto con precio para ${orderType}:`, error);
      throw error;
    }
  }
};

export default MenuService;